import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title, Meta } from '@angular/platform-browser';
import { AppConfig } from './app-config.model';
import { DEFAULT_APP_CONFIG } from './app-config.defaults';
import { firstValueFrom, catchError, of } from 'rxjs';

function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

function deepMerge(target: any, source: any): any {
  if (!source) return target;
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  private static config: AppConfig;

  constructor(
    private http: HttpClient,
    private titleService: Title,
    private metaService: Meta
  ) {}

  public loadAppConfig(): Promise<void> {
    const configUrl = 'assets/config/app-config.json';
    return firstValueFrom(
      this.http.get<AppConfig>(configUrl).pipe(
        catchError(error => {
          console.error(`Error loading app config from ${configUrl}`, error);
          return of(null);
        })
      )
    ).then((loadedConfig: AppConfig | null) => {
      const mergedConfig: AppConfig = deepMerge(DEFAULT_APP_CONFIG, loadedConfig || {});
      AppConfigService.config = mergedConfig;
      this.applyTheme(mergedConfig.theme);
      this.applyBranding(mergedConfig);
      this.loadGoogleFonts(mergedConfig.theme?.typography?.googleFontsUrl);
    });
  }

  private loadGoogleFonts(url?: string): void {
    if (!url) return;
    let link: HTMLLinkElement | null = document.querySelector('link#dynamic-google-fonts');
    if (!link) {
      link = document.createElement('link');
      link.id = 'dynamic-google-fonts';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = url;
  }

  static get getConfig(): AppConfig {
    return AppConfigService.config;
  }

  get config(): AppConfig {
    return AppConfigService.config;
  }

  private applyTheme(theme: any): void {
    const root = document.documentElement;
    
    // Helper to flatten objects to CSS variables
    const flattenObject = (obj: any, prefix = '--sys') => {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key];
          // Convert camelCase to kebab-case
          const kebabKey = key.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
          const varName = `${prefix}-${kebabKey}`;
          
          if (typeof value === 'object' && value !== null) {
            flattenObject(value, varName);
          } else {
            root.style.setProperty(varName, String(value));
          }
        }
      }
    };

    flattenObject(theme);
  }

  private applyBranding(config: AppConfig): void {
    if (config.appInfo?.title) {
      this.titleService.setTitle(config.appInfo.title);
      this.metaService.updateTag({ property: 'og:title', content: config.appInfo.title });
      this.metaService.updateTag({ name: 'twitter:title', content: config.appInfo.title });
    }
    
    if (config.appInfo?.description) {
      this.metaService.updateTag({ name: 'description', content: config.appInfo.description });
      this.metaService.updateTag({ property: 'og:description', content: config.appInfo.description });
      this.metaService.updateTag({ name: 'twitter:description', content: config.appInfo.description });
    }

    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary' });

    const previewImage = config.branding?.logos?.primary || config.branding?.favicon;
    if (previewImage) {
      this.metaService.updateTag({ property: 'og:image', content: previewImage });
      this.metaService.updateTag({ name: 'twitter:image', content: previewImage });
    }

    if (config.branding?.favicon) {
      this.setFavicon(config.branding.favicon);
    }
  }

  private setFavicon(faviconUrl: string): void {
    let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.type = 'image/png';
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;

    let appleTouchLink: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']");
    if (!appleTouchLink) {
      appleTouchLink = document.createElement('link');
      appleTouchLink.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchLink);
    }
    appleTouchLink.href = faviconUrl;
  }
}

